import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useI18n } from '../lib/i18n';
import { calculateBasePrice, DEFAULT_PRICE_CONFIG, loadPriceConfig, type PriceConfig } from '../lib/priceService';

export default function BookingCTA() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [arrivalDate, setArrivalDate] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [priceConfig, setPriceConfig] = useState<PriceConfig>(DEFAULT_PRICE_CONFIG);

  useEffect(() => {
    const loadPrices = async () => {
      try {
        const config = await loadPriceConfig();
        setPriceConfig(config);
      } catch (error) {
        console.error('Failed to load prices, using defaults:', error);
      }
    };
    loadPrices();
  }, []);

  const handleSubmit = () => {
    if (arrivalDate && departureDate) {
      navigate(`/buchen?arrivalDate=${arrivalDate}&departureDate=${departureDate}&step=2`);
    } else {
      navigate('/buchen');
    }
  };

  // Calculate days for preview
  const calculateDays = () => {
    if (!arrivalDate || !departureDate) return 0;
    const start = new Date(arrivalDate);
    const end = new Date(departureDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1;
  };

  const days = calculateDays();

  // Pricing: use active DB config (fallback to defaults)
  const getBasePrice = (d: number): number => calculateBasePrice(d, priceConfig);

  const totalPrice = getBasePrice(days);

  return (
    <section className="relative bg-gradient-to-br from-[#0052CC] to-[#0747A6] py-20 md:py-28 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.1),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(0,188,197,0.15),transparent_50%)]" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 text-white/90 text-sm font-medium mb-4">
              {t.bookingCTA.badge}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t.bookingCTA.title}
            </h2>
            <p className="text-lg text-white/80 max-w-xl mx-auto">
              {t.bookingCTA.subtitle}
            </p>
          </motion.div>

          {/* Booking Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-2xl p-6 md:p-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Arrival Date */}
              <div>
                <label className="block text-sm font-medium text-[#172B4D] mb-2">
                  <Icon icon="lucide:plane-takeoff" className="inline w-4 h-4 mr-2 text-[#0052CC]" />
                  {t.bookingCTA.arrivalLabel}
                </label>
                <input
                  type="date"
                  value={arrivalDate}
                  onChange={(e) => setArrivalDate(e.target.value)}
                  min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  className="w-full border border-gray-200 rounded-xl px-4 py-4 text-[#172B4D] focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] transition-all"
                />
                <p className="text-xs text-gray-500 mt-1">{t.bookingCTA.bookingNote}</p>
              </div>

              {/* Departure Date */}
              <div>
                <label className="block text-sm font-medium text-[#172B4D] mb-2">
                  <Icon icon="lucide:plane-landing" className="inline w-4 h-4 mr-2 text-[#0052CC]" />
                  {t.bookingCTA.departureLabel}
                </label>
                <input
                  type="date"
                  value={departureDate}
                  onChange={(e) => setDepartureDate(e.target.value)}
                  min={arrivalDate ? new Date(new Date(arrivalDate).getTime() + 86400000).toISOString().split('T')[0] : new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  className="w-full border border-gray-200 rounded-xl px-4 py-4 text-[#172B4D] focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] transition-all"
                />
              </div>
            </div>

            {/* Price Preview */}
            {days > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Icon icon="lucide:badge-check" className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-bold text-green-800">
                      {t.bookingCTA.daysParking.replace('{days}', String(days)).replace('{plural}', days !== 1 ? 'e' : '')}
                    </p>
                    <p className="text-sm text-green-600">{t.bookingCTA.valetIncluded}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-700">
                    {totalPrice.toFixed(2).replace('.', ',')} €
                  </p>
                  <p className="text-xs text-green-600">{t.bookingCTA.days1to8}</p>
                </div>
              </motion.div>
            )}

            {/* Submit Button */}
            <motion.button
              onClick={handleSubmit}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-[#0052CC] text-white font-bold text-lg py-4 rounded-xl shadow-lg shadow-blue-500/30 hover:bg-[#0747A6] transition-colors flex items-center justify-center gap-2"
            >
              <span>{t.bookingCTA.ctaButton}</span>
              <Icon icon="lucide:arrow-right" className="w-5 h-5" />
            </motion.button>

            {/* Trust Badges */}
            <div className="mt-6 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
                            <div className="flex items-center gap-2">
                <Icon icon="lucide:clock" className="w-4 h-4 text-[#0052CC]" />
                <span>{t.bookingCTA.freeCancellation}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon icon="lucide:car" className="w-4 h-4 text-[#0052CC]" />
                <span>{t.bookingCTA.valetService}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
